export type Posicao = 'G' | 'L' | 'Z' | 'M' | 'A';

export const POSICOES: readonly Posicao[] = ['G', 'L', 'Z', 'M', 'A'];

/** Divisão do Brasileirão em que o clube está (só nos jogos com acesso e rebaixamento): 1 = Série A, 2 = Série B. */
export type DivisaoClube = 1 | 2;

export interface Jogador {
  id: number;
  nome: string;
  idade: number;
  posicao: Posicao;
  /** 0..100 */
  overall: number;
  /** 0..100; cansa jogando e recupera entre rodadas */
  energia: number;
  /** salário mensal */
  salario: number;
  valor: number;
  /** rodadas restantes fora por lesão */
  lesaoRodadas: number;
  /** rodadas restantes suspenso */
  suspensaoRodadas: number;
  /** amarelos acumulados desde a última suspensão */
  amarelosAcumulados: number;
  /**
   * Cartões valem por campeonato: o vermelho na liga tira o jogador do próximo jogo DA LIGA, não da copa (e vice-versa). Estes quatro
   * guardam a contagem de cada um; `suspensaoRodadas` e `amarelosAcumulados` mostram a do campeonato da PRÓXIMA rodada (ver
   * sincronizarCartoes em jogo.ts), que é a que decide se o jogador pode ser escalado agora.
   */
  suspLiga?: number;
  suspCopa?: number;
  amarelosLiga?: number;
  amarelosCopa?: number;
  /** cartões da Libertadores (também contam à parte) */
  suspLib?: number;
  amarelosLib?: number;
  gols: number;
  jogos: number;
  assistencias: number;
  /** soma das notas dos jogos disputados (nota média = notaSoma / jogos) */
  notaSoma: number;
  /** lado preferencial: E (esquerdo) ou D (direito) */
  lado: 'E' | 'D';
  /** duas características (índices em CARACTERISTICAS) */
  caracteristicas: [number, number];
  /** dados reais (Série A 2026) */
  nacionalidade?: string;
  numeroCamisa?: number;
  /** campos deduzidos por falta de fonte (ex.: "idade", "posição") */
  dadosEstimados?: string;
  /** dias restantes de contrato (v4); cai 7 por rodada */
  contratoDias?: number;
  /** juniores: talento 1..10 (define o teto) e desenvolvimento 0..100 (sobe por semana até os 20 anos) */
  talento?: number;
  desenvolvimento?: number;
  /** nome original, guardado quando o usuário define um apelido */
  nomeCompleto?: string;
  /** habilidades individuais (calculadas sob demanda por habilidadesDe) */
  habilidades?: import('./habilidades').Habilidades;
  /** função detalhada (volante, ponta...); quando ausente é deduzida por funcaoDe */
  funcao?: import('./posicoes').Funcao;
  /** progresso acumulado do treino rumo ao próximo ponto de força */
  progressoTreino?: number;
}

export type TipoTrofeu = 'nacional' | 'copa' | 'estadual' | 'internacional';

/** Título conquistado (galeria de troféus). `divisao` 1 = elite. */
export interface Trofeu {
  temporada: number;
  competicao: string;
  tipo: TipoTrofeu;
  divisao: number;
}

/** Receitas e despesas acumuladas na temporada. */
export interface Balanco {
  bilheteria: number;
  patrocinio: number;
  premios: number;
  salarios: number;
  /** compras (negativo) e vendas (positivo) de jogadores na temporada */
  transferencias: number;
  /** detalhamento (v4): total vendido, total comprado, obras do estádio, multas de rescisão, juros do empréstimo */
  vendas?: number;
  compras?: number;
  estadio?: number;
  multas?: number;
  juros?: number;
}

export type Formacao = '4-4-2' | '4-3-3' | '4-5-1' | '3-5-2' | '5-3-2' | '3-4-3' | '5-4-1'
  | '4-2-3-1' | '4-1-4-1' | '4-3-1-2' | '4-4-1-1' | '4-2-4' | '4-3-2-1' | '3-4-2-1' | '3-4-1-2' | '3-6-1' | '5-2-3';
/** Mentalidade do time: retranca (super defensiva), defensivo (contra-ataque), equilibrado, ofensivo (propositivo) e total (tudo ou nada). */
export type Estilo = 'retranca' | 'defensivo' | 'equilibrado' | 'ofensivo' | 'total';
/** Agressividade nos desarmes: leve (cautelosa), normal e forte (carrinhos: mais cartões e mais desgaste). */
export type Marcacao = 'leve' | 'normal' | 'forte';
export type ConcentracaoAtaque = 'meio' | 'lados' | 'equilibrado';
/** Estilo de passe: curto (posse), misto, longo (ligação direta). */
export type Passe = 'curto' | 'misto' | 'longo';
/** Pressão / altura do bloco: baixa (espera no próprio campo), média, alta (sufoca a saída de bola, mas cansa). */
export type Pressao = 'baixa' | 'media' | 'alta';
/** Função individual de um titular (ver taticas.ts): muda o que o jogador faz em campo, não só a força. */
export type Papel =
  | 'libero'
  | 'rebatedor'
  | 'construtor'
  | 'lateral-apoiador'
  | 'lateral-invertido'
  | 'volante-fixo'
  | 'regista'
  | 'box-to-box'
  | 'mezala'
  | 'ponta-construtor'
  | 'extremo'
  | 'atacante-interior'
  | 'falso9'
  | 'referencia'
  | 'oportunista';
export const LISTA_PAPEIS: readonly Papel[] = ['libero', 'rebatedor', 'construtor', 'lateral-apoiador', 'lateral-invertido', 'volante-fixo', 'regista', 'box-to-box', 'mezala', 'ponta-construtor', 'extremo', 'atacante-interior', 'falso9', 'referencia', 'oportunista'];
/** Máximo de funções individuais ativas ao mesmo tempo (evita empilhar bônus). */
export const MAX_PAPEIS = 4;

/** Características de jogador (códigos 0..13 do original). Só Fin e Pas/Arm têm efeito leve no motor. */
export const CARACTERISTICAS = ['Col', 'DPe', 'Ref', 'SGo', 'Arm', 'Cab', 'Cru', 'Des', 'Dri', 'Fin', 'Mar', 'Pas', 'Res', 'Vel'] as const;
export const CARACTERISTICAS_NOME = [
  'Colocação', 'Defesa Penalty', 'Reflexo', 'Saída Gol', 'Armação', 'Cabeceio', 'Cruzamento',
  'Desarme', 'Drible', 'Finalização', 'Marcação', 'Passe', 'Resistência', 'Velocidade',
] as const;

/** Quando uma substituição ou ajuste planejado vale, comparando o placar do seu time com o do adversário naquele momento. */
export type CondicaoJogo = 'sempre' | 'perdendo' | 'empatando' | 'ganhando';

/**
 * Substituição planejada (vale para o jogo solo e para o online, onde a rodada roda sem os técnicos na frente da tela).
 * `minuto` é o relógio do jogo: 0 = intervalo (começo do 2º tempo), 1..90 = minuto.
 */
export interface PlanoSub {
  saiId: number;
  entraId: number;
  minuto: number;
  condicao: CondicaoJogo;
}

/** Mudança de estilo, marcação e concentração de ataque no intervalo, se a condição de placar for verdadeira. */
export interface AjusteIntervalo {
  condicao: CondicaoJogo;
  estilo?: Estilo;
  marcacao?: Marcacao;
  ataque?: ConcentracaoAtaque;
  passe?: Passe;
  pressao?: Pressao;
}

/** Funções especiais de jogadores em campo (como o menu do clique direito do Escalar Time original). */
export interface Funcoes {
  capitaoId?: number | null;
  batedorPenaltiId?: number | null;
  batedorFaltaId?: number | null;
  batedorEscanteioId?: number | null;
  /** o primeiro atacante recua e passa a contar no meio-campo (efeito próprio: o original não foi decodificado) */
  falso9?: boolean;
  /** função individual de cada titular (id do jogador -> função); vale só enquanto ele está em campo na posição da função */
  papeis?: Record<number, Papel>;
}

export const MAX_PLANO_SUBS = 5;
export const MAX_AJUSTES = 3;

export interface Tatica extends Funcoes {
  /** 'auto' escolhe a melhor formação a cada rodada */
  formacao: Formacao | 'auto';
  estilo: Estilo;
  /** marcação forte defende um pouco melhor, mas leva mais cartões */
  marcacao: Marcacao;
  /** por onde o time concentra os ataques */
  ataque: ConcentracaoAtaque;
  /** estilo de passe e pressão (ausentes = misto e média, como nos saves antigos) */
  passe?: Passe;
  pressao?: Pressao;
  /** ids dos 11 titulares, na ordem dos slots da formação; null = escalação automática */
  titulares: number[] | null;
  /** banco escolhido à mão (até 12, ver BANCO_MAX); ausente ou vazio = banco automático */
  reservas?: number[] | null;
  /** substituições e mudanças táticas planejadas para a partida */
  substituicoes?: PlanoSub[];
  ajustes?: AjusteIntervalo[];
}

export interface Clube {
  id: number;
  nome: string;
  sigla: string;
  /** divisão atual (jogos com acesso e rebaixamento: sobe e desce a cada temporada) */
  divisao?: DivisaoClube;
  /** id da liga estrangeira de que o clube vem (ligas-mundo.ts), nos jogos de uma liga estrangeira; escudo e estádio seguem o nome, não a sigla */
  liga?: string;
  /** nome do estádio, quando a fonte traz (clubes de liga estrangeira) */
  estadio?: string;
  /** item Bicho da loja: bônus de força (fração) de todos os jogadores na próxima partida do clube; some depois dela */
  bonusPartida?: number;
  /** força do clube, escala 6..20 como no jogo original */
  nivel: number;
  /** 1..5 */
  reputacao: number;
  caixa: number;
  capacidade: number;
  cor1: string;
  cor2: string;
  humano: boolean;
  elenco: Jogador[];
  /** preços dos ingressos por setor */
  precos: [number, number, number, number];
  balanco: Balanco;
  tatica: Tatica;
  /** 0..100 */
  confiancaDiretoria: number;
  /** 0..100 */
  confiancaTorcida: number;
  /** quando verdadeiro, os preços dos ingressos seguem sempre os sugeridos */
  usarPrecosSugeridos: boolean;
  /** técnico real do clube (Série A 2026) */
  tecnico?: string;
  /** academia de juniores (até 20) e galeria de troféus (v4) */
  juniores?: Jogador[];
  trofeus?: Trofeu[];
  /** dívida do empréstimo bancário (v4) */
  emprestimo?: number;
  /** plano de treino (foco do time, intensidade e treinos individuais) */
  treino?: import('./treino').Treino;
}

export interface Escalacao {
  titulares: Jogador[];
  reservas: Jogador[];
  /** posição que cada titular ocupa em campo (mesma ordem de `titulares`); ausente = a posição natural de cada um */
  slots?: Posicao[];
  estilo?: Estilo;
  marcacao?: Marcacao;
  ataque?: ConcentracaoAtaque;
  passe?: Passe;
  pressao?: Pressao;
  plano?: PlanoSub[];
  ajustes?: AjusteIntervalo[];
  funcoes?: Funcoes;
}
